import { kbd, KeyMap, KBHandler } from './keyboard';
import {
    set_alt,
    set_control,
    set_gui,
    set_shift,
} from './keypress';
import { set_button_states } from './non_graphical_ui_actions';
import {
    animate,
    hide_debug_objs,
    rotate_one_step_right_or_left,
    rotate_one_step_up_or_down,
    unhighlight_currently_highlighted_object,
    view_all,
} from './scene';
import app from './application-container';
import {
    debug_core_version_info,
    //debug_wacky_shaders,
    delete_all_2_vertices,
    delete_all_2_vertices_preserve_shape,
    display_toggle_edge_visibility,
    display_toggle_face_visibility,
    display_toggle_vertex_visibility,
    handle_escape,
    uid_add_or_abort_edge_drawing,
    uid_add_vertex,
    uid_attempt_save,
    uid_clone_selected_object,
    uid_delete_selected_objects,
    uid_deselect_all_objects,
    uid_fix_objects,
    uid_fix_or_unfix_tangent_at_vertex,
    uid_flip_rigid_move_manipulator_x,
    uid_flip_rigid_move_manipulator_y,
    uid_flip_rigid_move_manipulator_z,
    uid_global_manipulator_coordinates,
    uid_hide_midpoint_crosshair,
    uid_hide_rigid_move_manipulator,
    uid_highlight_edge_midpoint,
    uid_local_manipulator_coordinates,
    uid_make_edges_symmetric_if_possible,
    uid_make_selected_edges_linear_as_one,
    uid_perform_crosshair,
    uid_quick_scale,
    uid_redo,
    uid_select_all_objects,
    uid_set_linearity_of_selected_edges,
    uid_show_rigid_or_tangent_manipulator,
    uid_smoothen_selected_objects,
    uid_split_patch_selected_faces,
    uid_start_offset_face_if_possible,
    uid_toggle_drawing_edge_on_face,
    uid_toggle_editing_edge_tangent_mode,
    uid_toggle_snapping_to_object,
    uid_undo,
    uid_unfix_tangent_at_vertex,
    execute_boolean_operation,
    execute_hardcoded_boolean_operation,
} from './ui_display';
import { display_message_to_user, LOG } from '@phi/logging/frontend';
import {
    console_log,
    delete_selected_reference_partstudio_body,
    delete_selected_reference_stl_body,
    display_set_smoothen_strength_modal,
    run_consistency_checks,
    scale_selected_reference_body,
    suppress_ui_messages,
    translate_selected_reference_body,
} from './utility';
import { show_floating_toolbar } from '@features/floating-toolbar';
import { toggle_edge_curvatures } from '@features/curvature-combs';
import { show_smoothen_options_modal } from '@features/smoothness';
import { uid_popout_one_or_many } from '@features/popin-popout';
import { display_scale_modal } from '@phi/scale';
import {
    attach_surface_to_reference_model_stage1,
    attach_surface_to_reference_model_stage2,
    attach_surface_to_reference_model_stage3
} from './import_3d_scan_data';
import { frontend_service_container } from '@phi/frontend/service-container';

// Uncomment for core-debug
//import { debugvarsmodal, startdomagic} from './debug';

const toggle_hidden_edges = () => app.state.hidden_edges_visible = !app.state.hidden_edges_visible;

const empty = new Map();

const prevent = (f: KBHandler) => (e: KeyboardEvent) => {
    f(e);
    e.preventDefault();
};

const onlyforpro = (f: KBHandler) => (e: KeyboardEvent) => {
    if (frontend_service_container().subscriptions.state.subscription_status.value === 'starter')
        frontend_service_container().subscriptions.show_pro_feature_modal();
    else
        f(e);
};

const show_search_modal = prevent(() => app.state.modal.searchModal = true);

const do_nothing = () => undefined;

/** normal major mode */
export const normal: KeyMap = {
    down: new Map([
        [ kbd('Backspace'), () => uid_delete_selected_objects(false) ],
        [ kbd('Delete'), () => uid_delete_selected_objects(false) ],
        [ kbd('S-Backspace'), () => uid_delete_selected_objects(true) ],
        [ kbd('S-Delete'), () => uid_delete_selected_objects(true) ],
        [ kbd('Shift'), () => {
            set_shift(true);
            uid_highlight_edge_midpoint();
        } ],
        [ kbd('Control'), () => set_control(true) ],
        [ kbd('Alt'), () => set_alt(true) ],
        [ kbd('GUI'), () => set_gui(true) ],
        [ kbd('x'), () => {
            if (app.state.editing_edge_tangent_mode)
                uid_fix_or_unfix_tangent_at_vertex();
            else
                uid_fix_objects(false);
        } ],
        [ kbd('S-x'), () => {
            if (app.state.editing_edge_tangent_mode)
                uid_unfix_tangent_at_vertex();
            else
                uid_fix_objects(true);
        } ],
        [ kbd('f'), () => view_all(false) ],
        [ kbd('S-f'), () => view_all(true) ],
        [ kbd('C-f'), show_search_modal ],
        [ kbd('G-f'), show_search_modal ],
        [ kbd('m'), uid_show_rigid_or_tangent_manipulator ],
        [ kbd('e'), () => uid_add_or_abort_edge_drawing(false) ],
        [ kbd('S-e'), () => uid_add_or_abort_edge_drawing(true) ],
        [ kbd('q'), () => display_toggle_vertex_visibility() ],
        [ kbd('h'), toggle_hidden_edges ],
        [ kbd('r'), () => {
            display_toggle_face_visibility();
            animate();
        } ],
        [ kbd('b'), () => uid_state.brush_grabbing_objects = true ],
        [ kbd('j'), () => display_message_to_user(
            LOG.WARN,
            'The shortcut for the new, improved snapping is \'N\'. Please select object to snap, hit N, then click on target.'
        ) ],
        [ kbd('t'), uid_toggle_editing_edge_tangent_mode ],
        [ kbd('Space'), show_floating_toolbar ],
        [ kbd('w'), () => display_toggle_edge_visibility(false, false) ],
        [ kbd('Escape'), () => handle_escape(false, false, false) ],
        [ kbd('S-Escape'), () => handle_escape(false, true, true) ],

        /* Uncomment for core-debug:
        [ kbd('C-v'), () => debugvarsmodal() ],
        [ kbd('C-m'), () => startdomagic(false) ],
        [ kbd('C-S-m'), () => startdomagic(true) ],
        */

        //[ kbd('S-4'), () => properly_split_invisible_face_connected_components([], false, true) ],

        [ kbd('C-y'), () => uid_redo() ],
        [ kbd('G-y'), prevent(() => uid_redo()) ],
        [ kbd('y'), () => uid_make_edges_symmetric_if_possible() ],
        [ kbd('i'), () => run_consistency_checks(false, true) ],
        [ kbd('S-i'), () => run_consistency_checks(true, true) ],
        [ kbd('a'), () => uid_select_all_objects(true) ],
        [ kbd('S-a'), () => uid_deselect_all_objects() ],
        [ kbd('C-z'), () => uid_undo() ],
        [ kbd('G-z'), () => uid_undo() ],
        [ kbd('c'), uid_toggle_drawing_edge_on_face ],
        [ kbd('S-c'), uid_toggle_drawing_edge_on_face ],
        [ kbd('C-c'), uid_clone_selected_object ],
        [ kbd('G-c'), uid_clone_selected_object ],
        [ kbd('p'), uid_popout_one_or_many ],
        [ kbd('s'), onlyforpro(() => uid_split_patch_selected_faces(false)) ],
        [ kbd('S-s'), onlyforpro(() => uid_split_patch_selected_faces(true)) ],
        [ kbd('C-s'), prevent(uid_attempt_save) ],
        [ kbd('G-s'), prevent(uid_attempt_save) ],
        [ kbd('S-r'), () => uid_perform_crosshair('R') ],
        [ kbd('S-g'), () => uid_perform_crosshair('G') ],
        [ kbd('S-b'), () => uid_perform_crosshair('B') ],
        [ kbd('n'), uid_toggle_snapping_to_object ],
        [ kbd('l'), () => {
            hide_debug_objs(true);
            console_log('Hide debug objects');
            uid_set_linearity_of_selected_edges(true);
            animate();
        }],
        [ kbd('S-l'), () => {
            hide_debug_objs(true);
            console_log('Hide debug objects');
            uid_make_selected_edges_linear_as_one();
            animate();
        }],
        [ kbd('ArrowRight'), () => rotate_one_step_right_or_left(false) ],
        [ kbd('ArrowLeft'), () => rotate_one_step_right_or_left(true) ],
        [ kbd('ArrowUp'), () => rotate_one_step_up_or_down(true) ],
        [ kbd('ArrowDown'), () => rotate_one_step_up_or_down(false) ],
        [ kbd('v'), uid_add_vertex ],
        [ kbd('S-v'), uid_add_vertex ],
        [ kbd('2'), delete_all_2_vertices ],
        [ kbd('S-2'), delete_all_2_vertices_preserve_shape ],
        [ kbd('S-6'), suppress_ui_messages ],
        [ kbd('S-7'), () => execute_boolean_operation('union') ],
        [ kbd('S-8'), execute_hardcoded_boolean_operation ],
        //[ kbd('S-8'), check_tangent_edge_angles ],
        [ kbd('S-9'), debug_core_version_info ],
        [ kbd('S-d'), () => {
            unhighlight_currently_highlighted_object();
            display_scale_modal('');
        } ],
        [ kbd('d'), uid_quick_scale ],
        [ kbd('C-u'), prevent(display_set_smoothen_strength_modal) ],
        [ kbd('u'), uid_smoothen_selected_objects ],
        [ kbd('S-u'), uid_smoothen_selected_objects ],
        [ kbd('S-C-u'), show_smoothen_options_modal ],
        [ kbd('k'), toggle_edge_curvatures ],
        [ kbd('o'), uid_start_offset_face_if_possible ],
    ]),

    up: new Map([
        [ kbd('Shift'), () => {
            uid_hide_midpoint_crosshair();
            set_shift(false);
        } ],
        [ kbd('Control'), () => set_control(false) ],
        [ kbd('Alt'), () => set_alt(false) ],
        [ kbd('GUI'), () => set_gui(false) ],
        [ kbd('b'), () => {
            uid_state.brush_grabbing_objects = false;
            set_button_states();
        }],
    ]),
};

/** editing partstudio minor mode */
export const partstudio: KeyMap = {
    down: new Map([
        [ kbd('Backspace'), delete_selected_reference_partstudio_body ],
        [ kbd('Delete'), delete_selected_reference_partstudio_body ],
        [ kbd('x'), () => translate_selected_reference_body(1, 0, 0) ],
        [ kbd('S-x'), () => translate_selected_reference_body(-1, 0, 0) ],
        [ kbd('e'), do_nothing ],
        [ kbd('S-e'), do_nothing ],
        [ kbd('h'), do_nothing ],
        [ kbd('j'), do_nothing ],
        [ kbd('w'), do_nothing ],
        [ kbd('y'), () => translate_selected_reference_body(0, 1, 0) ],
        [ kbd('S-y'), () => translate_selected_reference_body(0, -1, 0) ],
        [ kbd('z'), () => translate_selected_reference_body(0, 0, 1) ],
        [ kbd('S-z'), () => translate_selected_reference_body(0, 0, -1) ],

        [ kbd('ArrowUp'), () => {
            const f = viewSize / 300;
            scale_selected_reference_body(1+f, 1+f, 1+f);
            animate();
        } ],
        [ kbd('ArrowDown'), () => {
            const f = viewSize / 300;
            scale_selected_reference_body(1/(1+f), 1/(1+f), 1/(1+f));
            animate();

        } ],

        [ kbd('1'), () => {
            const f = viewSize / 300;
            scale_selected_reference_body(1+f, 1, 1);
            animate();
        } ],
        [ kbd('S-1'), () => {
            const f = viewSize / 300;
            scale_selected_reference_body(1/(1+f), 1, 1);
            animate();
        } ],
        [ kbd('2'), () => {
            const f = viewSize / 300;
            scale_selected_reference_body(1, 1+f, 1);
            animate();
        } ],
        [ kbd('S-2'), () => {
            const f = viewSize / 300;
            scale_selected_reference_body(1, 1/(1+f), 1);
            animate();
        } ],
        [ kbd('3'), () => {
            const f = viewSize / 300;
            scale_selected_reference_body(1, 1, 1+f);
            animate();
        } ],
        [ kbd('S-3'), () => {
            const f = viewSize / 300;
            scale_selected_reference_body(1, 1, 1/(1+f));
            animate();
        } ],
        [ kbd('o'), do_nothing ],
    ]),

    up: empty,
};

/** editing stl ref minor mode */
export const stl: KeyMap = {
    down: new Map([
        [ kbd('Backspace'), delete_selected_reference_stl_body ],
        [ kbd('Delete'), delete_selected_reference_stl_body ],
        [ kbd('x'), () => translate_selected_reference_body(1, 0, 0) ],
        [ kbd('S-x'), () => translate_selected_reference_body(-1, 0, 0) ],
        [ kbd('e'), do_nothing ],
        [ kbd('S-e'), do_nothing ],
        [ kbd('h'), do_nothing ],
        [ kbd('j'), do_nothing ],
        [ kbd('w'), do_nothing ],
        [ kbd('y'), () => translate_selected_reference_body(0, 1, 0) ],
        [ kbd('S-y'), () => translate_selected_reference_body(0, -1, 0) ],
        [ kbd('z'), () => translate_selected_reference_body(0, 0, 1) ],
        [ kbd('S-z'), () => translate_selected_reference_body(0, 0, -1) ],

        [ kbd('1'), () => {
            const f = viewSize / 300;
            scale_selected_reference_body(1+f, 1, 1);
            animate();
        } ],
        [ kbd('S-1'), () => {
            const f = viewSize / 300;
            scale_selected_reference_body(1/(1+f), 1, 1);
            animate();
        } ],
        [ kbd('2'), () => {
            const f = viewSize / 300;
            scale_selected_reference_body(1, 1+f, 1);
            animate();
        } ],
        [ kbd('S-2'), () => {
            const f = viewSize / 300;
            scale_selected_reference_body(1, 1/(1+f), 1);
            animate();
        } ],
        [ kbd('3'), () => {
            const f = viewSize / 300;
            scale_selected_reference_body(1, 1, 1+f);
            animate();
        } ],
        [ kbd('S-3'), () => {
            const f = viewSize / 300;
            scale_selected_reference_body(1, 1, 1/(1+f));
            animate();
        } ],
        [ kbd('o'), do_nothing ],
    ]),

    up: empty,
};

/** rigid move manipulator minor mode */
export const manipulator: KeyMap = {
    down: new Map([
        [ kbd('x'), uid_flip_rigid_move_manipulator_x ],
        [ kbd('y'), uid_flip_rigid_move_manipulator_y ],
        [ kbd('z'), uid_flip_rigid_move_manipulator_z ],
        [ kbd('m'), () => uid_hide_rigid_move_manipulator(false, 0, true) ],
        [ kbd('e'), do_nothing ],
        [ kbd('S-e'), do_nothing ],
        [ kbd('b'), do_nothing ],
        [ kbd('j'), do_nothing ],
        [ kbd('w'), do_nothing ],
        [ kbd('g'), uid_global_manipulator_coordinates ],
        [ kbd('l'), uid_local_manipulator_coordinates ],
        [ kbd('o'), do_nothing ],
    ]),

    up: empty,
};

/** editing sketch ref minor mode */
export const sketch: KeyMap = {
    down: new Map([
        [ kbd('r'), do_nothing ],
        [ kbd('j'), do_nothing ],
        [ kbd('w'), do_nothing ],
        [ kbd('o'), do_nothing ],
    ]),

    up: empty,
};

/** insert major mode */
export const insert: KeyMap = {
    down: empty,
    up: empty,
};

/** attaching to stl major mode */
export const attaching_to_stl = {
    down: new Map([
        [ kbd('1'), () => attach_surface_to_reference_model_stage1() ],
        [ kbd('2'), () => attach_surface_to_reference_model_stage2(1) ],
        [ kbd('S-2'), () => attach_surface_to_reference_model_stage2(2) ],
        [ kbd('C-2'), () => attach_surface_to_reference_model_stage2(3) ],
        [ kbd('3'), () => attach_surface_to_reference_model_stage3() ],
    ]),

    up: empty,
};
